import { useState, useMemo } from "react";
import CustomPopUp from "../global/CustomPopup";
import Button from "../organism/Button";
import WarningIcon from "../../assets/svg/WarningIcon";
import IndeterminateCheckbox from "../Tables/IndeterminateCheckbox";
import useDeclineOrder from "../../services/utils/custom hooks/Approvals/useDeclineOrder";
import CustomText from "../atomic/CustomText";

export interface OrderToDecline {
  id: string | number;
  patientFirstName?: string;
  patientLastName?: string;
}

interface DeclineEsopredictOrderPopupProps {
  orders: OrderToDecline[];
  onClose: () => void;
  onDeclineSuccess?: () => void;
}

function DeclineEsopredictOrderPopup({
  orders,
  onClose,
  onDeclineSuccess,
}: Readonly<DeclineEsopredictOrderPopupProps>): JSX.Element {
  const [selectedIds, setSelectedIds] = useState<string[] | number[]>(() =>
    orders.map((o:any) => o.id)
  );
  const [acknowledged, setAcknowledged] = useState(false);
  const { mutateAsync: declineOrder, isPending } = useDeclineOrder();

  const toggleOrder = (id: string | number) => {
    setSelectedIds((prev:any) =>
      prev.includes(id) ? prev.filter((x:any) => x !== id) : [...prev, id]
    );
  };

  const confirmationText = useMemo(() => {
    if (orders.length === 0) return "";
    if (orders.length === 1) {
      const o = orders[0];
      const name = [o.patientFirstName, o.patientLastName].filter(Boolean).join(" ") || "this patient";
      return `Are you sure you want to decline ordering Esopredict test for patient '${name}'?`;
    }
    return "Are you sure you want to decline the following Esopredict order(s)? You can deselect any order below.";
  }, [orders]);

  const canConfirm = acknowledged && selectedIds.length > 0;

  const handleConfirmDecline = async () => {
    if (!canConfirm || isPending) return;
    try {
      const selectedOrders = orders.filter((o) =>
        (selectedIds as (string | number)[]).includes(o.id)
      );
      await declineOrder({
        orderIds: selectedIds,
        orderDetails: selectedOrders.map((o) => ({
          id: o.id,
          patientFirstName: o.patientFirstName,
          patientLastName: o.patientLastName,
        })),
      });
      onClose();
      onDeclineSuccess?.();
    } catch {
      // Error toast handled by hook
    }
  };

  return (
    <CustomPopUp
      popUpTitle="Decline Esopredict Order"
      hidePopUp={onClose}
    >
      <div className="flex flex-col gap-4 w-full max-w-[88vw] md:max-w-none md:min-w-[min(420px,92vw)] box-border">
        <div className="flex items-start gap-3 w-full">
          <WarningIcon className="shrink-0 mt-0.5" stroke="#fe4f4f" />
          <CustomText
            text={confirmationText}
            fontWeight="font-semibold"
            fontSize="text-sm"
            className="text-royalBlue max-w-prose"
          />
        </div>

        {orders.length > 1 && (
          <div className="border border-roundedInputBorder rounded-lg p-3 bg-sideNavGray max-h-48 overflow-auto w-full">
            <CustomText
              text="Select orders to decline:"
              fontWeight="font-semibold"
              fontSize="text-sm"
              className="text-royalBlue mb-2"
            />
            {orders.map((order:any) => {
              const name = [order.patientFirstName, order.patientLastName].filter(Boolean).join(" ") || "Patient";
              const isChecked = selectedIds.includes(order?.id as never);
              return (
                <label
                  key={String(order.id)}
                  className="flex items-center gap-2 py-1.5 cursor-pointer"
                >
                  <IndeterminateCheckbox
                    checked={isChecked}
                    onChange={() => toggleOrder(order.id)}
                    inlineForCenter
                  />
                  <span className="text-sm text-royalBlue">
                    Esopredict for {name}
                  </span>
                </label>
              );
            })}
          </div>
        )}

<div className="w-full flex justify-center mt-2">
  <label className="flex items-center gap-3 cursor-pointer">
    <IndeterminateCheckbox
      checked={acknowledged}
      onChange={(e) => setAcknowledged((e.target as HTMLInputElement).checked)}
      inlineForCenter
    />
    <span className="text-sm font-semibold text-royalBlue">
      I acknowledge that I am declining the selected order(s).
    </span>
  </label>
</div>

        <div className="flex justify-end gap-3 pt-2 w-full">
          <Button
            buttonTheme="secondary"
            textInformation={{
              text: "Cancel",
              fontWeight: "font-semibold",
            }}
            onClick={onClose}
            disabled={isPending}
          />
          <Button
            buttonTheme="primary"
            textInformation={{
              text: "Confirm Decline",
              fontWeight: "font-semibold",
            }}
            onClick={handleConfirmDecline}
            disabled={!canConfirm || isPending}
            loading={isPending}
          />
        </div>
      </div>
    </CustomPopUp>
  );
}

export default DeclineEsopredictOrderPopup;
